/**
 * Reportes · Destrucciones — operaciones tipo despacho_destruccion.
 *
 * Resolución correcta de producto: Series → GTIN → Producto (no `mov.detalle`,
 * que era string libre — fix B2 de Paquete G).
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useOperaciones, useProductos, useSeries } from '@/api/queries';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/format';

export function DestruccionesTab() {
  const { data: operaciones = [], isLoading } = useOperaciones();
  const { data: productos = [] } = useProductos();
  const { data: series = [] } = useSeries();

  const destrucciones = useMemo(
    () =>
      operaciones
        .filter((o) => o.tipo === 'despacho_destruccion')
        .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()),
    [operaciones],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando...
      </div>
    );
  }
  if (destrucciones.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-gray-600">
          Sin destrucciones registradas. Confirmar un despacho de tipo "Destrucción" va a
          aparecer acá.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="overflow-x-auto rounded border border-gray-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Op</TableHead>
                <TableHead>Cantidad</TableHead>
                <TableHead>Productos</TableHead>
                <TableHead>Estado SNT</TableHead>
                <TableHead>Código Tx</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {destrucciones.map((op) => {
                const gtins = new Set(
                  op.seriesIds
                    .map((sid) => series.find((s) => s.id === sid)?.gtin)
                    .filter((g): g is string => Boolean(g)),
                );
                const nombres = Array.from(gtins)
                  .map((g) => productos.find((p) => p.gtin === g)?.nombre)
                  .filter((n): n is string => Boolean(n));
                return (
                  <TableRow key={op.id}>
                    <TableCell className="font-mono text-[11px]">
                      {fmtDateTime(op.fecha)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{op.id}</TableCell>
                    <TableCell className="text-xs">{op.seriesIds.length}</TableCell>
                    <TableCell className="text-xs">
                      {nombres.length > 0 ? nombres.join(', ') : `${gtins.size} producto(s)`}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'badge',
                          op.anmat.estado === 'confirmada' || op.anmat.estado === 'informada'
                            ? 'badge-green'
                            : op.anmat.estado === 'cancelada'
                              ? 'badge-gray'
                              : op.anmat.estado === 'error'
                                ? 'badge-red'
                                : 'badge-blue',
                        )}
                      >
                        {op.anmat.estado}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-gray-600">
                      {op.anmat.codigoTransaccion ?? '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
